import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Outlet, useRouterState } from '@tanstack/react-router'
import { ToastProvider } from 'shared/ui/ToastProvider'
import { markInitialRouteRendered } from 'shared/lib/initialRouteRenderState'
import { hideBootSplash } from 'shared/lib/bootSplash'
import { InitialMapSplashProvider } from 'shared/ui/InitialMapSplashContext'
import { SplashScreen } from 'shared/ui/SplashScreen'
import { WebviewHeader } from 'shared/ui/WebviewHeader'
import { markMapPerformance } from 'shared/lib/mapPerformance'

const SPLASH_STARTUP_WATCHDOG_MS = 4_000

function isMapSplashPath(pathname: string) {
  return pathname.startsWith('/floor-6/map') || pathname.startsWith('/webview')
}

export function RootLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const [startedOnSplashRoute] = useState(() => isMapSplashPath(pathname))
  const [isSplashDismissed, setIsSplashDismissed] = useState(false)
  const isSplashDismissedRef = useRef(false)
  const splashWatchdogTimerRef = useRef<number | null>(null)
  const splashPaintFrameRef = useRef<number | null>(null)
  const splashCompositeFrameRef = useRef<number | null>(null)
  const isSplashRoute = isMapSplashPath(pathname)
  const isInitialMapSplashVisible = startedOnSplashRoute && isSplashRoute && !isSplashDismissed

  const finishInitialMapSplashExit = useCallback(() => {
    if (isSplashDismissedRef.current) return
    isSplashDismissedRef.current = true

    if (splashWatchdogTimerRef.current !== null) {
      window.clearTimeout(splashWatchdogTimerRef.current)
      splashWatchdogTimerRef.current = null
    }
    if (splashPaintFrameRef.current !== null) {
      window.cancelAnimationFrame(splashPaintFrameRef.current)
      splashPaintFrameRef.current = null
    }
    if (splashCompositeFrameRef.current !== null) {
      window.cancelAnimationFrame(splashCompositeFrameRef.current)
      splashCompositeFrameRef.current = null
    }

    hideBootSplash()
    setIsSplashDismissed(true)
  }, [])

  useLayoutEffect(() => {
    // React splash (or the destination route) is in the same commit. Drop the
    // pre-JS HTML cover here so there is never a frame with neither splash.
    hideBootSplash()
  }, [])

  useEffect(() => {
    markInitialRouteRendered()
  }, [])

  useEffect(() => {
    if (!isInitialMapSplashVisible || splashWatchdogTimerRef.current !== null) return undefined

    splashWatchdogTimerRef.current = window.setTimeout(() => {
      splashWatchdogTimerRef.current = null
      markMapPerformance('splash-watchdog-timeout')
      finishInitialMapSplashExit()
    }, SPLASH_STARTUP_WATCHDOG_MS)

    return () => {
      if (splashWatchdogTimerRef.current !== null) {
        window.clearTimeout(splashWatchdogTimerRef.current)
        splashWatchdogTimerRef.current = null
      }
    }
  }, [finishInitialMapSplashExit, isInitialMapSplashVisible])

  useEffect(() => {
    return () => {
      if (splashWatchdogTimerRef.current !== null) {
        window.clearTimeout(splashWatchdogTimerRef.current)
      }
      if (splashPaintFrameRef.current !== null) {
        window.cancelAnimationFrame(splashPaintFrameRef.current)
      }
      if (splashCompositeFrameRef.current !== null) {
        window.cancelAnimationFrame(splashCompositeFrameRef.current)
      }
    }
  }, [])

  const dismissInitialMapSplash = useCallback(() => {
    if (
      isSplashDismissedRef.current ||
      splashPaintFrameRef.current !== null ||
      splashCompositeFrameRef.current !== null
    ) {
      return
    }

    // Let both canvas layers reach the compositor before removing the only
    // startup cover. This prevents a one-frame white map shell in WebViews.
    splashPaintFrameRef.current = window.requestAnimationFrame(() => {
      splashPaintFrameRef.current = null
      splashCompositeFrameRef.current = window.requestAnimationFrame(() => {
        splashCompositeFrameRef.current = null
        markMapPerformance('splash-handoff')
        finishInitialMapSplashExit()
      })
    })
  }, [finishInitialMapSplashExit])

  const handleClose = () => {
    window.CLOSE_WEBVIEW?.postMessage('close')

    if (window.CLOSE_WEBVIEW) return

    if (window.history.length > 1) {
      window.history.back()
      return
    }

    if (pathname.startsWith('/webview') || pathname.startsWith('/floor-6/map')) {
      window.location.assign('/auth')
      return
    }

    window.close()
  }

  return (
    <InitialMapSplashProvider value={dismissInitialMapSplash}>
      <ToastProvider>
        <div className="flex h-[var(--app-height,100svh)] flex-col bg-transparent!">
          <WebviewHeader title="Карта" onBack={handleClose} />
          <main className="relative flex flex-1 min-h-0 flex-col overflow-hidden bg-white">
            <div className="relative min-h-0 flex-1">
              <Outlet />
            </div>
          </main>
        </div>
        {isInitialMapSplashVisible ? (
          <div className="fixed inset-0 z-100">
            <SplashScreen />
          </div>
        ) : null}
      </ToastProvider>
    </InitialMapSplashProvider>
  )
}
